/* ============================================================================
 * 应用壳：顶栏 / 导航轨 / 画布 / 副驾 / 状态栏
 * ========================================================================== */
import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import { NAV, FLAT_NAV, findNav } from './nav';
import { PAGES } from './pages';
import { useUi } from '@/store/ui';
import { useBackend } from '@/store/backend';
import { useSession, useRole, roleInfo } from '@/store/session';
import { useAuth, onUnauthorized } from '@/store/auth';
import { setUnauthorizedHandler } from '@/lib/api';
import Login from './Login';
import { ROLES, META } from '@/domain/master';
import { Icon } from '@/components/Icon';
import { CommandPalette } from '@/components/CommandPalette';
import { ToastHost, toast } from '@/components/Toast';
import { EmptyState, Skeleton } from '@/components/primitives';
import { Copilot } from '@/components/Copilot';
import { SettingsMenu } from '@/components/SettingsMenu';

/* ── 顶栏 ───────────────────────────────────────────────────────────── */

function Breadcrumb() {
  const loc = useLocation();
  const route = loc.pathname.split('/').filter(Boolean)[0] ?? 'home';
  const param = loc.pathname.split('/').filter(Boolean)[1];
  const item = findNav(route);
  if (!item) return null;
  return (
    /* F-55：V2 面包屑是纯文本、0 个链接、cursor:auto，无法点回上级 */
    <nav className="crumb" aria-label="面包屑">
      <NavLink to="/home">工作台</NavLink>
      {item.groupName && (
        <>
          <span className="sep">/</span>
          <span className="muted">{item.groupName}</span>
        </>
      )}
      {route !== 'home' && (
        <>
          <span className="sep">/</span>
          {param ? <NavLink to={`/${route}`}>{item.name}</NavLink> : <span className="last">{item.name}</span>}
        </>
      )}
      {param && (
        <>
          <span className="sep">/</span>
          <span className="last mono">{decodeURIComponent(param)}</span>
        </>
      )}
    </nav>
  );
}

function TopBar() {
  const { theme, toggleTheme, toggleNav, ai, toggleAi } = useUi();

  return (
    <header className="topbar">
      <button className="iconbtn" onClick={toggleNav} aria-label="折叠或展开导航">
        <Icon name="menu" />
      </button>

      <div className="brand">
        <div className="embermark" aria-hidden="true">索</div>
        <div className="col" style={{ gap: 0 }}>
          <div className="brand-name">C+APS <span style={{ color: 'var(--clay-text)' }}>V3</span></div>
          <div className="brand-sub">索通智能计划与排产平台</div>
        </div>
      </div>

      <Breadcrumb />
      <span className="spacer" />

      {/* B-01：求解内核性质如实标注。真 CP-SAT 服务接通前不得写「CP-SAT 整数规划」。 */}
      <SolverChip />

      <WhoAmI />

      <button className="iconbtn" onClick={toggleTheme} aria-label={theme === 'dark' ? '切换到浅色主题' : '切换到深色主题'}>
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      </button>

      <SettingsMenu />

      <button
        className="iconbtn"
        onClick={toggleAi}
        aria-label={ai === 'open' ? '收起 AI 决策助手' : '展开 AI 决策助手'}
        aria-pressed={ai === 'open'}
      >
        <Icon name="bot" />
      </button>
    </header>
  );
}

/** 求解引擎状态标识（B-01 / B-04）——由 /api/health 真实自检决定，不写死 */
function SolverChip() {
  const info = useBackend((s) => s.info);
  const isReal = info.kernel === 'cpsat';
  return (
    <span className={`solverchip${isReal ? ' is-real' : ''}`} title={
      isReal
        ? `求解内核：OR-Tools CP-SAT ${info.ortools_version ?? ''}（真实约束求解，可证明最优性间隙）`
        : '求解内核：演示版启发式（后端未连通）。还原了 CP-SAT 的约束语义与决策日志形态，但不是真实 OR-Tools 内核，耗时与一致性数字仅供演示。'
    }>
      <i className="dot" aria-hidden="true" />
      求解内核 <b>{isReal ? 'CP-SAT' : '演示内核'}</b>
    </span>
  );
}

/** 当前登录者（A4）。
 *  这里曾经是一个角色下拉框——选中即生效、服务端不校验。
 *  现在角色来自服务端签发的令牌，前端改不了，只能退出重登。 */
function WhoAmI() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const ephemeral = useAuth((s) => s.ephemeral);
  const role = useRole();
  if (!user) return null;
  return (
    <div className="whoami">
      <span className="whoami-main">
        <Icon name="users" size={13} />
        <b>{user.display_name}</b>
        <span className="t-aux">{ROLES[role]?.name ?? user.role}</span>
      </span>
      {ephemeral && (
        <span className="t-aux" title="服务端未配置 CAPS_SECRET，本次使用进程内随机密钥：后端重启后需要重新登录。生产部署必须显式配置。">
          <Icon name="warn" size={12} /> 临时密钥
        </span>
      )}
      <button className="iconbtn" onClick={() => { logout(); toast('已退出登录', 'ok'); }}
        aria-label="退出登录" title="退出登录">
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

/* ── 导航轨 ─────────────────────────────────────────────────────────── */

function Rail() {
  const { nav, openGroups, toggleGroup, ensureGroupOpen, toggleNav } = useUi();
  const navAllowed = useSession((s) => s.navAllowed);
  const loc = useLocation();
  const route = loc.pathname.split('/').filter(Boolean)[0] ?? 'home';
  const collapsed = nav === 'collapsed';

  // 当前路由所在分组自动展开
  useEffect(() => {
    const item = findNav(route);
    if (item?.groupKey) ensureGroupOpen(item.groupKey);
  }, [route, ensureGroupOpen]);

  return (
    <nav className="rail" aria-label="模块导航">
      <div className="railnav">
        {NAV.map((n) => {
          if (!('group' in n) || !n.group) {
            const leaf = n as { route: string; name: string; icon: 'home' };
            if (!navAllowed(leaf.route)) return null;
            return (
              <NavLink key={leaf.route} to={`/${leaf.route}`}
                className={({ isActive }) => `navlink${isActive ? ' on' : ''}`}>
                <span className="ico"><Icon name={leaf.icon} /></span>
                <span className="label">{leaf.name}</span>
                {/* 折叠态的模块名来源。始终留在 DOM 里，读屏两种状态都读得到；
                    不用原生 title —— 它要悬停约 1 秒才弹，且样式由操作系统画 */}
                <span className="tip">{leaf.name}</span>
              </NavLink>
            );
          }
          const kids = n.children.filter((c) => navAllowed(c.route));
          if (kids.length === 0) return null;
          const isOpen = collapsed || openGroups.includes(n.group);
          // 组内有活动页时组标题上色：折叠着也能一眼看出人在哪个域
          const hasActive = kids.some((c) => c.route === route);
          return (
            <div className="navgroup" key={n.group}>
              <button
                className={`navgroup-hd${hasActive ? ' has-on' : ''}`}
                onClick={() => toggleGroup(n.group)}
                aria-expanded={isOpen}
              >
                <span className="ico"><Icon name={n.icon} /></span>
                <span className="label">{n.name}</span>
                <span className="cnt">{kids.length}</span>
                <Icon name="right" size={13} className="chev" />
              </button>
              {/* .navkids 是层级的视觉载体（缩进 + 左引导线），不能省成裸列表 */}
              {isOpen && (
                <div className="navkids">
                  {kids.map((c) => (
                    <NavLink key={c.route} to={`/${c.route}`}
                      className={({ isActive }) => `navlink${isActive ? ' on' : ''}`}>
                      <span className="ico"><Icon name={c.icon} size={15} /></span>
                      <span className="label">{c.name}</span>
                      <span className="tip">{c.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button className="railtoggle" onClick={toggleNav} aria-label={collapsed ? '展开导航' : '收起导航'}>
        <Icon name={collapsed ? 'right' : 'left'} size={14} />
        <span className="label">收起导航</span>
      </button>
    </nav>
  );
}

/* ── 状态栏 ─────────────────────────────────────────────────────────── */

function StatusBar() {
  return (
    <footer className="statusbar">
      <span>数据口径：<b>真实主数据</b>（基地/产品/BOM/客户 取证于企业档案）+ <b>演示业务量</b></span>
      <span className="vsep" />
      {/* X-01~X-03：外部对接按计划后置，此处如实标注 */}
      <span>集成：NCC <b>未接</b> · MES <b>未接</b> · 行情 <b>未接</b></span>
      <span className="spacer" />
      <span>{META.appName} {META.version} · 计划月 {META.planMonth} · 今日 {META.today}</span>
    </footer>
  );
}

/* ── 路由承载 ───────────────────────────────────────────────────────── */

function PageHost() {
  const loc = useLocation();
  const params = useParams();
  const route = loc.pathname.split('/').filter(Boolean)[0] ?? 'home';
  const navAllowed = useSession((s) => s.navAllowed);
  const role = useRole();

  /* F-54：V2 全站 27 页 document.title 恒定，多标签页/历史/收藏均不可辨 */
  useEffect(() => {
    const item = findNav(route);
    const seg = params.id ? ` ${params.id}` : '';
    document.title = item ? `${item.name}${seg} · C+APS V3` : 'C+APS V3 · 索通智能计划与排产平台';
  }, [route, params.id]);

  if (!navAllowed(route)) {
    return (
      <EmptyState
        kind="forbidden"
        hint={`当前角色「${roleInfo(role).name}」不具备该模块权限。需要访问请联系管理员调整角色，或换用有权限的账号登录。`}
      />
    );
  }

  const Page = PAGES[route];
  if (!Page) return <EmptyState kind="no-data" title="页面不存在" hint={`未注册路由 /${route}`} />;

  return (
    <Suspense fallback={<div style={{ padding: 'var(--sp-4)' }}><Skeleton rows={6} height={18} /></div>}>
      <Page />
    </Suspense>
  );
}

/* ── App ────────────────────────────────────────────────────────────── */

export default function App() {
  const syncDom = useUi((s) => s.syncDom);
  const checkBackend = useBackend((s) => s.check);
  const restore = useAuth((s) => s.restore);
  const ready = useAuth((s) => s.ready);
  const user = useAuth((s) => s.user);

  useEffect(() => { syncDom(); }, [syncDom]);
  useEffect(() => { void checkBackend(); }, [checkBackend]);
  // 令牌复核必须在渲染主界面之前完成，否则会先闪一下有权限的界面再跳登录
  useEffect(() => {
    setUnauthorizedHandler(onUnauthorized);
    void restore();
  }, [restore]);

  if (!ready) {
    return (
      <div className="bootwrap">
        <div className="logincard"><Skeleton rows={3} /></div>
      </div>
    );
  }
  // A4：未登录不渲染任何业务界面。以前这里没有闸门——
  // 角色是浏览器里选的，等于人人都是运营总监。
  if (!user) return <><Login /><ToastHost /></>;

  return (
    <div className="app">
      <TopBar />
      <div className="shell">
        <Rail />
        <main className="canvas" id="main">
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            {FLAT_NAV.map((n) => (
              <Route key={n.route} path={`/${n.route}`} element={<PageHost />} />
            ))}
            {/* F-56：V2 深链参数被解析但页面不消费。V3 参数进入路由并由页面读取。 */}
            {FLAT_NAV.map((n) => (
              <Route key={n.route + ':id'} path={`/${n.route}/:id`} element={<PageHost />} />
            ))}
            <Route path="*" element={<EmptyState kind="no-data" title="页面不存在" hint="检查地址是否正确，或用 ⌘K 搜索" />} />
          </Routes>
        </main>
        <Copilot />
      </div>
      <StatusBar />
      <CommandPalette />
      <ToastHost />
    </div>
  );
}
